import { FilterField, labelId } from "kontrolEdge/helpers/advancedFilters";
import { keListTagFilterHaystack } from "kontrolEdge/components/Shared/mapKeListResourceTags";
import {
  accessTypeLabel,
  carrierMediumLabel,
  formatCarrierAccessNames,
} from "kontrolEdge/helpers/carrierAccess";
import {
  ExtenderPop,
  cpeRolePresentation,
  extenderPopStatusPresentation,
} from "./ExtenderPopData";

// Date parser: accept DD/MM/YYYY, YYYY-MM-DD and ISO (same rules as Ports).
function parseDateInput(raw: string): Date | null {
  const s = raw.trim();
  const m1 = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m1) {
    const [, dd, mm, yyyy] = m1;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(d.getTime()) ? null : d;
  }
  const m2 = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (m2) {
    const [, yyyy, mm, dd] = m2;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function makeBaseExtenderPopFieldSpecs(): FilterField<ExtenderPop>[] {
  return [
    {
      label: "Extended PoP ID",
      key: "extenderpopid",
      accessor: (r) => r.serviceId,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Name",
      key: "name",
      accessor: (r) => r.name,
      type: "string",
      inFreeText: true,
    },
    // Filter on the label users see in the pill, not the raw enum value.
    {
      label: "Status",
      key: "status",
      accessor: (r) => extenderPopStatusPresentation(r.status).label,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Tag",
      key: "tag",
      accessor: (r) => keListTagFilterHaystack(r),
      type: "string",
      inFreeText: true,
    },
    {
      // Display names plus the raw codes, so "ConsoleConnect" and "CC" find
      // the same rows — same rule the Ports haystack follows. The medium too,
      // so "Satellite" finds the sites reached by satellite (CTX-800), and the
      // sold access by its 84- id and type.
      label: "Carrier Access",
      key: "carrieraccess",
      accessor: (r) =>
        [
          formatCarrierAccessNames(r.carrierAccess),
          ...r.carrierAccess.map((c) => c.carrierCode),
          ...r.carrierAccess.map((c) => carrierMediumLabel(c.medium)),
          ...r.carrierAccess.flatMap((c) =>
            c.carrierAccess
              ? [c.carrierAccess.serviceId, accessTypeLabel(c.carrierAccess.accessType)]
              : []
          ),
        ]
          .filter(Boolean)
          .join(" "),
      type: "string",
      inFreeText: true,
    },
    {
      // Every CPE of the site: its service id, name and role, so "80-08615E"
      // and "Hot-Standby" both find the row.
      label: "CPE",
      key: "cpe",
      accessor: (r) =>
        r.cpes
          .flatMap((cpe) => [
            cpe.serviceId,
            cpe.name,
            cpeRolePresentation(cpe.role)?.label ?? "",
          ])
          .filter(Boolean)
          .join(" "),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Term",
      key: "term",
      accessor: (r) => r.term,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Renew Date",
      key: "renewdate",
      accessor: (r) => r.renewDateRaw,
      type: "date",
      inFreeText: true,
      parseRhs: parseDateInput,
    },
  ];
}

export function makeExtenderPopFields(
  isViewingAllCustomers: boolean
): FilterField<ExtenderPop>[] {
  const base = makeBaseExtenderPopFieldSpecs();
  if (isViewingAllCustomers) {
    base.splice(1, 0, {
      label: "Customer",
      key: "customer",
      accessor: (r) =>
        [r.customer?.name ?? "", r.customer?.chargebeeCustomerId ?? ""]
          .filter(Boolean)
          .join(" "),
      type: "string",
      inFreeText: true,
    });
  }
  for (const f of base) f.key ||= labelId(f.label);
  return base;
}
